import { useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useSendContactMutation } from '../slices/contactApiSlice';

export default function ContactScreen() {
  const user = useSelector(state => state.auth.userInfo);
  const [form, setForm] = useState({ name: user?.name || '', email: user?.email || '', subject: '', message: '', website: '' });
  const [status, setStatus] = useState(null);
  const busy = useRef(false);
  const [sendContact, { isLoading }] = useSendContactMutation();
  const change = event => setForm(prev => ({ ...prev, [event.target.name]: event.target.value }));
  const submit = async event => {
    event.preventDefault();
    if (busy.current) return;
    busy.current = true;
    setStatus(null);
    try {
      const response = await sendContact(form).unwrap();
      setStatus({ success: true, message: response.message });
      setForm(prev => ({ ...prev, subject: '', message: '' }));
    } catch (error) {
      setStatus({ success: false, message: error?.data?.message || 'Unable to send your message. Please try again.' });
    } finally { busy.current = false; }
  };
  const inputClass = 'w-full rounded-xl border border-slate-300 bg-white px-3 py-3 text-slate-800 focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-100';
  return (
    <section className="mx-auto max-w-2xl rounded-3xl border border-slate-200 bg-gradient-to-br from-sky-50 via-white to-emerald-50 p-4 shadow-sm md:p-8">
      <h1 className="text-3xl font-semibold text-slate-900">Contact Us</h1>
      <p className="mt-3 text-slate-600">Have a question or need a hand? Send us a message and the Practidex team will get back to you.</p>
      <form onSubmit={submit} className="mt-6 space-y-4">
        <fieldset disabled={isLoading} className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div><label htmlFor="contact-name" className="mb-1 block font-medium">Name</label><input id="contact-name" name="name" autoComplete="name" required maxLength={100} value={form.name} onChange={change} className={inputClass} /></div>
            <div><label htmlFor="contact-email" className="mb-1 block font-medium">Email</label><input id="contact-email" name="email" type="email" autoComplete="email" required maxLength={254} value={form.email} onChange={change} className={inputClass} /></div>
          </div>
          <div><label htmlFor="contact-subject" className="mb-1 block font-medium">Subject</label><input id="contact-subject" name="subject" required maxLength={150} value={form.subject} onChange={change} className={inputClass} /></div>
          <div><label htmlFor="contact-message" className="mb-1 block font-medium">Message</label><textarea id="contact-message" name="message" required maxLength={5000} rows={6} value={form.message} onChange={change} className={inputClass} /><p className="mt-1 text-xs text-slate-500">Please leave out passwords and payment card details.</p></div>
          <div hidden aria-hidden="true"><label htmlFor="contact-website">Website</label><input id="contact-website" name="website" tabIndex={-1} autoComplete="off" value={form.website} onChange={change} /></div>
          <button type="submit" disabled={isLoading} className="inline-flex items-center gap-2 rounded-xl bg-sky-600 px-5 py-3 font-semibold text-white hover:bg-sky-700 disabled:opacity-60">
            {isLoading && <span aria-hidden="true" className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white" />}
            {isLoading ? 'Sending...' : 'Send message'}
          </button>
        </fieldset>
        {status && <p role={status.success ? 'status' : 'alert'} className={`rounded-xl p-3 ${status.success ? 'bg-emerald-50 text-emerald-800' : 'bg-rose-50 text-rose-800'}`}>{status.message}</p>}
      </form>
      <p className="mt-5 text-sm text-slate-600">You can also email <a href="mailto:contact@practidex.com" className="text-sky-700 underline">contact@practidex.com</a>.</p>
    </section>
  );
}
